import { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon, ArrowUpTrayIcon, CheckIcon, ClipboardDocumentIcon, DocumentTextIcon, ExclamationTriangleIcon,
  LockClosedIcon, PaperAirplaneIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import ChatMarkdown from '../components/ChatMarkdown';
import { kopiereFormatiert } from '../util/kopieren';
import {
  ArbeitstischAufgabe, ArbeitstischRunde, arbeitstischAufgaben, streamArbeitstisch, Unterlage, uploadUnterlage,
} from '../api/unimedica';

/**
 * Arbeitstisch (#2/#3, Wunsch Mario 08.10.2026): Unterlage oder Text hineinlegen, Aufgabe-Knopf drücken,
 * fertigen Text kopieren. Kein Patient, kein Internet, nichts wird gespeichert — nach dem Neuladen ist alles weg.
 */
const DATEITYPEN = '.pdf,.docx,.odt,.png,.jpg,.jpeg,.webp,.tiff,.tif,.heic,.csv,.md,.txt';
const MAX_UNTERLAGEN = 5;

type Antwort = { frage: string; text: string; fertig: boolean };

/** Für die Praxissoftware: Fettdruck und Überschriften-Zeichen weg, Aufzählungen bleiben. */
function alsKlartext(md: string): string {
  return md.replace(/\*\*(.+?)\*\*/g, '$1').replace(/^#{1,6}\s+/gm, '').trim();
}

export default function Arbeitstisch() {
  const [aufgaben, setAufgaben] = useState<ArbeitstischAufgabe[]>([]);
  const [unterlagen, setUnterlagen] = useState<Unterlage[]>([]);
  const [laedt, setLaedt] = useState(0);
  const [text, setText] = useState('');
  const [verlauf, setVerlauf] = useState<ArbeitstischRunde[]>([]);
  const [antworten, setAntworten] = useState<Antwort[]>([]);
  const [nachfrage, setNachfrage] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState<number | null>(null);
  const [ziehen, setZiehen] = useState(false);
  const ende = useRef<HTMLDivElement>(null);

  useEffect(() => { arbeitstischAufgaben().then(setAufgaben).catch(() => setFehler('Aufgaben konnten nicht geladen werden.')); }, []);
  useEffect(() => { ende.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [antworten.length]);

  // #13 (Mario 10.10.): Screenshot/Foto direkt aus der Zwischenablage einfügen (Cmd/Ctrl+V). Text im Textfeld
  // bleibt normales Einfügen — abgefangen werden nur Dateien.
  const dateienRef = useRef<(f: File[]) => void>(() => {});
  useEffect(() => {
    const beimEinfuegen = (ev: Event) => {
      const e = ev as ClipboardEvent;
      const dateien = Array.from(e.clipboardData?.items ?? [])
        .filter((i) => i.kind === 'file').map((i) => i.getAsFile()).filter((f): f is File => !!f);
      if (!dateien.length) return;
      e.preventDefault();
      const zeit = new Date().toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(/:/g, '.');
      dateienRef.current(dateien.map((f, i) => (f.name && f.name !== 'image.png') ? f
        : new File([f], `Bildschirmfoto ${zeit}${dateien.length > 1 ? `-${i + 1}` : ''}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type })));
    };
    window.addEventListener('paste', beimEinfuegen);
    return () => window.removeEventListener('paste', beimEinfuegen);
  }, []);

  const begonnen = verlauf.length > 0;

  const dateienHinzu = async (files: FileList | File[] | null) => {
    if (!files) return;
    const liste = Array.from(files).slice(0, MAX_UNTERLAGEN - unterlagen.length);
    setFehler(null);
    for (const f of liste) {
      setLaedt((n) => n + 1);
      try {
        const u = await uploadUnterlage(f);
        setUnterlagen((alt) => [...alt, u]);
      } catch (e: any) {
        setFehler(`${f.name}: ${String(e?.message ?? e).replace(/^uploadUnterlage \d+: /, '')}`);
      } finally {
        setLaedt((n) => n - 1);
      }
    }
  };

  dateienRef.current = (f: File[]) => { if (!begonnen) void dateienHinzu(f); };

  const ausfuehren = async (aufgabe: ArbeitstischAufgabe | null, freitext: string) => {
    if (laeuft) return;
    const ersteRunde = !begonnen;
    if (ersteRunde && !aufgabe && !freitext.trim() && unterlagen.length === 0) return;
    setFehler(null); setLaeuft(true);
    const frage = aufgabe ? aufgabe.name : freitext.trim();
    const idx = antworten.length;
    setAntworten((a) => [...a, { frage, text: '', fertig: false }]);
    let kontext = '';
    let antwort = '';
    await streamArbeitstisch(
      {
        aufgabe_id: aufgabe?.id ?? null,
        text: freitext,
        unterlage_file_ids: ersteRunde ? unterlagen.map((u) => u.file_id) : [],
        verlauf,
      },
      (k) => { kontext = k; },
      (t) => { antwort += t; setAntworten((a) => a.map((x, i) => (i === idx ? { ...x, text: antwort } : x))); },
      () => {
        setAntworten((a) => a.map((x, i) => (i === idx ? { ...x, fertig: true } : x)));
        setVerlauf((v) => [...v, { role: 'user', content: kontext }, { role: 'assistant', content: antwort }]);
        if (ersteRunde) setUnterlagen([]);        // auf dem Server gelesen und gelöscht
        setNachfrage('');
      },
      (e) => {
        setFehler(e);
        setAntworten((a) => a.filter((_, i) => i !== idx));
        if (ersteRunde) setUnterlagen([]);        // die Dateien sind auch bei einem Fehler schon gelöscht
      },
    );
    setLaeuft(false);
  };

  const neu = () => {
    setUnterlagen([]); setText(''); setVerlauf([]); setAntworten([]); setNachfrage(''); setFehler(null);
  };

  const ausgaben = useRef<Record<number, HTMLDivElement | null>>({});
  const kopieren = async (i: number, t: string) => {
    // Formatiert fuer Mail/Word, Textfassung ohne Steuerzeichen fuer siMed (21.10.2026)
    try {
      const el = ausgaben.current[i];
      if (el) await kopiereFormatiert(el); else await navigator.clipboard.writeText(alsKlartext(t));
      setKopiert(i); setTimeout(() => setKopiert(null), 1800);
    }
    catch { setFehler('Kopieren nicht möglich — bitte den Text markieren und kopieren.'); }
  };

  const knoepfe = (
    <div className="flex flex-wrap gap-2">
      {aufgaben.map((a) => (
        <button key={a.id} type="button" title={a.anweisung} disabled={laeuft || laedt > 0}
          onClick={() => void ausfuehren(a, begonnen ? '' : text)}
          className="btn-secondary min-h-10 px-3.5 text-sm disabled:opacity-50">{a.name}</button>
      ))}
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="page-h1 flex items-center gap-2"><DocumentTextIcon className="h-5 w-5 text-primary-500" />Arbeitstisch</h1>
            <p className="mt-1 text-xs leading-relaxed text-ps-dark-400">
              Unterlage oder Text hineinlegen, Aufgabe wählen, Ergebnis kopieren. Ohne Patient und ohne Akte.
            </p>
          </div>
          {begonnen && (
            <button type="button" onClick={neu} className="btn-ghost inline-flex min-h-10 items-center gap-1.5 text-sm">
              <ArrowPathIcon className="h-4 w-4" /> Neu beginnen
            </button>
          )}
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-700">
          <LockClosedIcon className="h-3.5 w-3.5 shrink-0" />
          Kein Internet, keine Akte — nichts wird gespeichert. Beim Neuladen ist alles weg.
        </p>
      </div>

      {!begonnen && (
        <div className="card space-y-3">
          <label
            onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
            onDragLeave={() => setZiehen(false)}
            onDrop={(e) => { e.preventDefault(); setZiehen(false); void dateienHinzu(e.dataTransfer.files); }}
            className={`flex min-h-24 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed px-4 py-5 text-center text-sm transition-colors ${ziehen ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'}`}>
            <ArrowUpTrayIcon className="h-6 w-6 text-ps-dark-400" />
            <span className="font-medium text-ps-dark-700">Visitenblatt, Brief oder Foto hier ablegen</span>
            <span className="text-xs text-ps-dark-400">oder antippen zum Auswählen · Screenshot mit Cmd/Ctrl+V einfügen · PDF, Word, Bild, Text · bis {MAX_UNTERLAGEN} Dateien</span>
            <input type="file" accept={DATEITYPEN} multiple className="hidden" disabled={unterlagen.length >= MAX_UNTERLAGEN}
              onChange={(e) => { void dateienHinzu(e.currentTarget.files); e.currentTarget.value = ''; }} />
          </label>

          {(unterlagen.length > 0 || laedt > 0) && (
            <ul className="space-y-1.5">
              {unterlagen.map((u) => (
                <li key={u.file_id} className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                  <DocumentTextIcon className="h-4 w-4 shrink-0 text-ps-dark-400" />
                  <span className="min-w-0 flex-1 truncate">{u.filename}</span>
                  {u.gelesen
                    ? <span className="text-[11px] text-ps-dark-400">{u.zeichen.toLocaleString('de-CH')} Zeichen gelesen</span>
                    : <span className="text-[11px] text-amber-700">nicht lesbar</span>}
                  <button type="button" aria-label="Entfernen" onClick={() => setUnterlagen((a) => a.filter((x) => x.file_id !== u.file_id))}
                    className="flex h-8 w-8 items-center justify-center rounded text-ps-dark-400 hover:bg-gray-200"><XMarkIcon className="h-4 w-4" /></button>
                </li>
              ))}
              {laedt > 0 && <li className="px-3 py-1 text-xs text-ps-dark-400">Wird gelesen …</li>}
            </ul>
          )}

          <textarea className="input-field text-sm" rows={4} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="… oder Text hier einfügen (z. B. eine E-Mail mit Rezeptwunsch)" />

          <div>
            <div className="mb-2 text-xs font-medium text-ps-dark-500">Was soll daraus werden?</div>
            {knoepfe}
          </div>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); void ausfuehren(null, nachfrage ? `${text ? `Text:\n${text}\n\n` : ''}${nachfrage}` : text); }}>
            <input className="input-field flex-1 text-sm" value={nachfrage} onChange={(e) => setNachfrage(e.target.value)}
              placeholder="Eigene Anweisung, z. B. „als SMS an den Patienten“" />
            <button type="submit" disabled={laeuft || laedt > 0 || (!nachfrage.trim() && !text.trim() && unterlagen.length === 0)}
              className="btn-primary inline-flex min-h-10 items-center gap-1.5 px-3.5 text-sm disabled:opacity-50">
              <PaperAirplaneIcon className="h-4 w-4" /> Los
            </button>
          </form>
        </div>
      )}

      {fehler && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" /><span>{fehler}</span>
        </div>
      )}

      {antworten.map((a, i) => (
        <div key={i} className="card">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-ps-dark-400">{a.frage}</span>
            {a.fertig && (
              <button type="button" onClick={() => void kopieren(i, a.text)}
                className="btn-primary inline-flex min-h-9 items-center gap-1.5 px-3 text-xs">
                {kopiert === i ? <><CheckIcon className="h-4 w-4" /> Kopiert</> : <><ClipboardDocumentIcon className="h-4 w-4" /> Kopieren</>}
              </button>
            )}
          </div>
          {a.text ? <div ref={(e) => { ausgaben.current[i] = e; }}><ChatMarkdown text={a.text} /></div> : <div className="text-sm text-ps-dark-400">Wird geschrieben …</div>}
        </div>
      ))}

      {begonnen && (
        <div className="card space-y-3">
          <div className="text-xs font-medium text-ps-dark-500">Weiter bearbeiten</div>
          {knoepfe}
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (nachfrage.trim()) void ausfuehren(null, nachfrage); }}>
            <input className="input-field flex-1 text-sm" value={nachfrage} onChange={(e) => setNachfrage(e.target.value)}
              placeholder="z. B. „kürzer“, „ohne Gewicht“, „als Brief an Dr. …“" />
            <button type="submit" disabled={laeuft || !nachfrage.trim()}
              className="btn-primary inline-flex min-h-10 items-center gap-1.5 px-3.5 text-sm disabled:opacity-50">
              <PaperAirplaneIcon className="h-4 w-4" /> Senden
            </button>
          </form>
        </div>
      )}
      <div ref={ende} />
    </div>
  );
}
